import React, { useEffect, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { notification, Skeleton, Select, Space, Pagination, Modal, Input, Button } from 'antd';
import { useTranslation } from 'react-i18next';

import { getStageTasks, updateTaskNoValidation } from '../../../Maintenance/helpers/apiCalls';

const LOCATION_ID = '67774a5de87ed72ad7ab1007';

const slugKey = (label) =>
  (label || '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .toLowerCase();

const ReportsByLocation = () => {
  const { instance } = useMsal();
  const { t } = useTranslation();
  const [notificationApi, contextHolder] = notification.useNotification();

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [codeFilter, setCodeFilter] = useState();
  const [statusFilter, setStatusFilter] = useState('Approved');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [subjectFilterInput, setSubjectFilterInput] = useState(subjectFilter);
  const [filenameFilter, setFilenameFilter] = useState('');
  const [filenameFilterInput, setFilenameFilterInput] = useState(filenameFilter);

  const fetchData = async (page = 1, pageSize = 10, overrides = {}) => {
    try {
      setLoading(true);
      const subjectValue = overrides.subject ?? subjectFilter;
      const filenameValue = overrides.filename ?? filenameFilter;
      const params = {
        'subject.location': LOCATION_ID,
        'report[exists]': true,
        page,
        limit: pageSize,
      };
      if (codeFilter) params.code = codeFilter;
      if (statusFilter) params['report.status'] = statusFilter;
      if (subjectValue?.trim()) params['subject.name[regex]'] = subjectValue.trim();
      if (filenameValue?.trim()) params['report.filename[regex]'] = filenameValue.trim();

      const response = await getStageTasks(instance, params);
      setItems(response?.items ?? []);
      setPagination((p) => ({ ...p, current: page, pageSize, total: response?.totalItems ?? 0 }));
    } catch (err) {
      notificationApi.error({
        message: t('database_communication_failure') || 'Database communication failure',
        description: err?.response?.data?.message
          ? `${err?.response?.data?.message} (${err?.response?.status})`
          : err?.message,
        duration: 5,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch when filters change
  useEffect(() => {
    fetchData(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeFilter, statusFilter]);

  const handleSearchClick = (e) => {
    if (e) e.preventDefault();
    if (loading) return;
    const subjectValue = subjectFilterInput;
    const filenameValue = filenameFilterInput;
    setSubjectFilter(subjectValue);
    setFilenameFilter(filenameValue);
    fetchData(1, pagination.pageSize, {
      subject: subjectValue,
      filename: filenameValue,
    });
  };

  const TARGET_PHASE_TITLE = ['Detalhes do Teste', 'Rastreabilidade'];
  const isTargetPhaseTitle = (title) => {
    const titles = Array.isArray(TARGET_PHASE_TITLE) ? TARGET_PHASE_TITLE : [TARGET_PHASE_TITLE];
    return titles.includes(title);
  };

  // Collect union of field labels across all visible items (prefers task.phases; falls back to latest report version content)
  const fieldLabels = useMemo(() => {
    const labels = [];
    items?.forEach((task) => {
      let phases = Array.isArray(task?.phases)
        ? task.phases
        : Array.isArray(task?.phase)
        ? task.phase
        : [];

      if (!phases?.length) {
        const versions = task?.report?.versions ?? [];
        const latestVersionNumber = task?.report?.latestVersion;
        const selectedVersion =
          versions.find((v) => v?.version === latestVersionNumber) || versions[versions.length - 1];
        phases = selectedVersion?.content ?? [];
      }

      phases
        ?.filter((phase) => Array.isArray(phase?.fields) && phase.fields.length > 0)
        ?.filter((phase) => isTargetPhaseTitle(phase?.title))
        ?.forEach((phase) => {
          phase.fields.forEach((f) => {
            const lbl = f?.label ?? '-';
            if (lbl && !labels.includes(lbl)) labels.push(lbl);
          });
        });
    });
    return labels;
  }, [items]);

  // simple table rendering only

  const editableFields = useMemo(() => fieldLabels.map((lbl) => slugKey(lbl)), [fieldLabels]);

  // Build table dataSource rows
  const rows = useMemo(() => {
    return (items || []).map((task) => {
      // Resolve phases from task.phases (or task.phase) with fallback to latest report version content
      let phases = Array.isArray(task?.phases)
        ? task.phases
        : Array.isArray(task?.phase)
        ? task.phase
        : [];

      let selectedVersion = null;
      if (!phases?.length) {
        const versions = task?.report?.versions ?? [];
        const latestVersionNumber = task?.report?.latestVersion;
        selectedVersion =
          versions.find((v) => v?.version === latestVersionNumber) || versions[versions.length - 1];
        phases = selectedVersion?.content ?? [];
      }

      const row = {
        key: task?._id ?? `${task?.code}-${task?.token}-${task?.sequential}`,
        _taskId: task?._id,
        subjectName: task?.report?.subject?.name ?? task?.subject?.name ?? '-',
        reportDate: task.report?.versions[0]?.date ?? task?.report?.date ?? null,
        reportFilename: task?.report?.filename ?? '-',
        reportName: `${task?.code ?? '-'}-${task?.token ?? '-'} #${task?.sequential ?? '-'}`,
      };

      phases
        ?.filter((phase) => Array.isArray(phase?.fields) && phase.fields.length > 0)
        ?.filter((phase) => isTargetPhaseTitle(phase?.title))
        ?.forEach((phase) => {
          phase.fields.forEach((f) => {
            const lbl = f?.label ?? '';
            const k = slugKey(lbl);
            const value = f?.userGivenValue;
            let display;
            if (Array.isArray(value)) display = value.join(', ');
            else if (value === null || value === undefined || value === '') display = '-';
            else display = String(value);
            // If duplicated labels appear across phases, prefer the first non-empty
            if (!row[k] || row[k] === '-') row[k] = display;
          });
        });

      return row;
    });
  }, [items]);

  // Local editable table state cloned from rows
  const [rowData, setRowData] = useState([]);
  useEffect(() => {
    setRowData(rows);
  }, [rows]);

  const [dirtyCells, setDirtyCells] = useState({}); // { [rowKey]: { [field]: value } }

  // Selection state for editable cells
  const [isSelecting, setIsSelecting] = useState(false);
  const [anchorCell, setAnchorCell] = useState(null); // { rowIndex, field }
  const [selectedCells, setSelectedCells] = useState(new Set()); // Set of `${rowIndex}:${field}`

  const fieldIndexMap = useMemo(() => {
    const map = {};
    editableFields.forEach((f, i) => (map[f] = i));
    return map;
  }, [editableFields]);

  useEffect(() => {
    const onUp = () => setIsSelecting(false);
    window.addEventListener('mouseup', onUp);
    return () => window.removeEventListener('mouseup', onUp);
  }, []);

  const keyFor = (rowIndex, field) => `${rowIndex}:${field}`;

  const startSelection = (rowIndex, field, e) => {
    if (!editableFields.includes(field)) return;
    const k = keyFor(rowIndex, field);
    if (e && (e.metaKey || e.ctrlKey)) {
      // toggle selection on ctrl/cmd click
      setSelectedCells((prev) => {
        const next = new Set(prev);
        if (next.has(k)) next.delete(k);
        else next.add(k);
        return next;
      });
      setAnchorCell({ rowIndex, field });
      return;
    }
    setAnchorCell({ rowIndex, field });
    setIsSelecting(true);
    setSelectedCells(new Set([k]));
  };

  const computeRectSelection = (rowIndex, field) => {
    if (!anchorCell) return null;
    if (!editableFields.includes(field)) return null;
    const ai = anchorCell.rowIndex;
    const af = anchorCell.field;
    const minR = Math.min(ai, rowIndex);
    const maxR = Math.max(ai, rowIndex);
    const ciA = fieldIndexMap[af] ?? 0;
    const ciB = fieldIndexMap[field] ?? 0;
    const minC = Math.min(ciA, ciB);
    const maxC = Math.max(ciA, ciB);
    const next = new Set();
    for (let r = minR; r <= maxR; r += 1) {
      for (let c = minC; c <= maxC; c += 1) {
        const f = editableFields[c];
        if (f) next.add(keyFor(r, f));
      }
    }
    return next;
  };

  const extendSelection = (rowIndex, field) => {
    if (!isSelecting || !anchorCell) return;
    const next = computeRectSelection(rowIndex, field);
    if (next) setSelectedCells(next);
  };

  const extendSelectionWithKeyboard = (rowIndex, field) => {
    if (!anchorCell) setAnchorCell({ rowIndex, field });
    const next = computeRectSelection(rowIndex, field);
    setSelectedCells(next);
  };

  const updateCell = (rowIndex, field, value) => {
    const sanitized = value == null ? '' : value;
    setRowData((prev) => {
      const next = [...prev];
      if (!next[rowIndex]) return prev;
      next[rowIndex] = { ...next[rowIndex], [field]: sanitized };
      const key = next[rowIndex]?.key;
      if (key) {
        setDirtyCells((prevDirty) => ({
          ...prevDirty,
          [key]: { ...(prevDirty[key] || {}), [field]: sanitized },
        }));
      }
      return next;
    });
  };

  const handlePasteMatrix = (e, startRowIndex, startField) => {
    const startColIndex = editableFields.indexOf(startField);
    if (startColIndex < 0) return; // not editable
    const text = e.clipboardData.getData('text/plain') || '';
    if (!text) return;
    e.preventDefault();
    const lines = text.replace(/\r/g, '').split('\n').filter((l) => l.length > 0);
    const matrix = lines.map((line) => line.split('\t'));

    setRowData((prev) => {
      const next = [...prev];
      for (let r = 0; r < matrix.length; r += 1) {
        const ri = startRowIndex + r;
        if (ri >= next.length) break;
        const row = { ...next[ri] };
        for (let c = 0; c < matrix[r].length; c += 1) {
          const fi = startColIndex + c;
          if (fi >= editableFields.length) break;
          const field = editableFields[fi];
          row[field] = matrix[r][c];
          const key = row.key || next[ri]?.key;
          // track dirty
          setDirtyCells((prevDirty) => ({
            ...prevDirty,
            [key]: { ...(prevDirty[key] || {}), [field]: matrix[r][c] },
          }));
        }
        next[ri] = row;
      }
      return next;
    });
  };

  const handlePaste = (e, rowIndex, field) => {
    const raw = e.clipboardData.getData('text/plain') || '';
    const text = raw.replace(/\r/g, '');
    const isMatrix = text.includes('\t') || text.includes('\n');
    if (isMatrix) return handlePasteMatrix(e, rowIndex, field);
    // single value paste
    e.preventDefault();
    const value = text;
    const sel = Array.from(selectedCells);
    if (sel.length > 1) {
      // apply to all selected editable cells
      sel.forEach((k) => {
        const [rStr, f] = k.split(':');
        const ri = parseInt(rStr, 10);
        if (!Number.isNaN(ri) && editableFields.includes(f)) updateCell(ri, f, value);
      });
    } else {
      updateCell(rowIndex, field, value);
    }
  };

  // Save logic
  const buildTaskPhaseWithEdits = (task, changesForTask) => {
    // derive phases as used to render
    let phases = Array.isArray(task?.phases)
      ? task.phases
      : Array.isArray(task?.phase)
      ? task.phase
      : [];

    if (!phases?.length) {
      const versions = task?.report?.versions ?? [];
      const latestVersionNumber = task?.report?.latestVersion;
      const selectedVersion =
        versions.find((v) => v?.version === latestVersionNumber) || versions[versions.length - 1];
      phases = selectedVersion?.content ?? [];
    }

    const next = JSON.parse(JSON.stringify(phases || []));
    const targetPhaseIndexes = (next || [])
      .map((p, i) => (isTargetPhaseTitle(p?.title) ? i : -1))
      .filter((i) => i >= 0);
    if (!targetPhaseIndexes.length) return next; // nothing to update

    Object.entries(changesForTask || {}).forEach(([fieldKey, newVal]) => {
      const v = newVal == null ? '' : newVal;
      targetPhaseIndexes.forEach((pi) => {
        const phase = next[pi];
        if (!Array.isArray(phase?.fields)) return;
        const fieldIndex = phase.fields.findIndex((f) => slugKey(f?.label ?? '') === fieldKey);
        if (fieldIndex < 0) return;
        const current = phase.fields[fieldIndex];
        // When blank, save empty string as requested
        if (Array.isArray(current?.userGivenValue)) {
          phase.fields[fieldIndex].userGivenValue = v === '' ? '' : [v];
        } else {
          phase.fields[fieldIndex].userGivenValue = v;
        }
      });
    });

    return next;
  };

  const [saving, setSaving] = useState(false);

  const saveChanges = async () => {
    const entries = Object.entries(dirtyCells || {});
    if (!entries.length) return;
    const taskByKey = {};
    items.forEach((t) => {
      const key = t?._id ?? `${t?.code}-${t?.token}-${t?.sequential}`;
      taskByKey[key] = t;
    });

      // Build patch per task id
      const patches = entries
        .map(([rowKey, fieldMap]) => {
          const task = taskByKey[rowKey];
          if (!task?._id) return null;
          const phase = buildTaskPhaseWithEdits(task, fieldMap);
          return { _id: task._id, phase };
        })
        .filter(Boolean);

    Modal.confirm({
      title: 'Confirm save',
      content: `Apply changes to ${patches.length} task(s)?`,
      okText: 'Save',
      cancelText: 'Cancel',
      onOk: async () => {
        setSaving(true);
        try {
          for (const p of patches) {
            await updateTaskNoValidation(instance, p);
          }
          notificationApi.success({ message: 'Changes saved', duration: 2 });
          setDirtyCells({});
          await fetchData(pagination.current, pagination.pageSize);
        } catch (err) {
          notificationApi.error({
            message: t('database_communication_failure') || 'Database communication failure',
            description: err?.response?.data?.message
              ? `${err?.response?.data?.message} (${err?.response?.status})`
              : err?.message,
            duration: 5,
          });
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const handleKeyDown = (e, rowIndex, field) => {
    // Clear selected cells with Backspace/Delete
    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault();
      const targets = selectedCells.size > 0 ? Array.from(selectedCells) : [`${rowIndex}:${field}`];
      targets.forEach((k) => {
        const [rStr, f] = k.split(':');
        const ri = parseInt(rStr, 10);
        if (!Number.isNaN(ri) && editableFields.includes(f)) updateCell(ri, f, '');
      });
      return;
    }

    if (!e.shiftKey) return;
    const colIndex = fieldIndexMap[field] ?? 0;
    let r = rowIndex;
    let c = colIndex;
    if (e.key === 'ArrowDown') r = Math.min(rowData.length - 1, rowIndex + 1);
    else if (e.key === 'ArrowUp') r = Math.max(0, rowIndex - 1);
    else if (e.key === 'ArrowRight') c = Math.min(editableFields.length - 1, colIndex + 1);
    else if (e.key === 'ArrowLeft') c = Math.max(0, colIndex - 1);
    else return;
    e.preventDefault();
    const targetField = editableFields[c];
    if (!targetField) return;
    if (!anchorCell) setAnchorCell({ rowIndex, field });
    extendSelectionWithKeyboard(r, targetField);
  };

  // Build simple table columns
  const columns = useMemo(() => {
    const base = [
      { title: 'Report Subject', dataIndex: 'subjectName', width: 260 },
      {
        title: 'Report Date',
        dataIndex: 'reportDate',
        width: 180,
        render: (v) => (v ? new Date(v).toLocaleString() : '-'),
      },
      { title: 'Filename', dataIndex: 'reportFilename', width: 240 },
    ];
    const fieldCols = fieldLabels.map((lbl) => ({
      title: lbl,
      dataIndex: slugKey(lbl),
      width: Math.min(Math.max((lbl || '').length * 9, 180), 360),
    }));
    return base.concat(fieldCols);
  }, [fieldLabels]);

  const totalWidth = useMemo(() => {
    return (columns || []).reduce((acc, c) => acc + (c.width || 200), 0);
  }, [columns]);
  const columnCount = columns?.length || 1;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {contextHolder}
      <h2>Reports by Location (temporary)</h2>
      <div style={{ fontSize: '0.9rem', color: '#666' }}>
        Location: <code>{LOCATION_ID}</code>
      </div>

      <Space wrap size="middle">
        <div>
          <div style={{ fontSize: 12, marginBottom: 6 }}>Code</div>
          <Select
            allowClear
            placeholder="All codes"
            style={{ width: 200 }}
            value={codeFilter}
            options={[
              { value: 'VFCB', label: 'VFCB' },
              { value: 'RTCB', label: 'RTCB' },
              { value: 'RTCM', label: 'RTCM' },
              { value: 'RVLF', label: 'RVLF' },
              { value: 'VFCM', label: 'VFCM' },
            ]}
            onChange={(val) => setCodeFilter(val)}
          />
        </div>
        <div>
          <div style={{ fontSize: 12, marginBottom: 6 }}>Report status</div>
          <Select
            allowClear
            placeholder="All statuses"
            style={{ width: 200 }}
            value={statusFilter}
            options={[{ value: 'Approved', label: 'Approved' }]}
            onChange={(val) => setStatusFilter(val)}
          />
        </div>
        <div>
          <div style={{ fontSize: 12, marginBottom: 6 }}>Report subject</div>
          <Input
            allowClear
            placeholder="Search subject name"
            style={{ width: 240 }}
            value={subjectFilterInput}
            onChange={(e) => setSubjectFilterInput(e.target.value)}
            onPressEnter={handleSearchClick}
          />
        </div>
        <div>
          <div style={{ fontSize: 12, marginBottom: 6 }}>Filename</div>
          <Input
            allowClear
            placeholder="Search filename"
            style={{ width: 240 }}
            value={filenameFilterInput}
            onChange={(e) => setFilenameFilterInput(e.target.value)}
            onPressEnter={handleSearchClick}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <Button type="primary" onClick={handleSearchClick} disabled={loading}>
            Search
          </Button>
        </div>
        <div style={{ flex: 1 }} />
        <div>
          <button
            disabled={!Object.keys(dirtyCells || {}).length || saving}
            onClick={saveChanges}
            style={{
              padding: '6px 12px',
              borderRadius: 6,
              border: '1px solid #1677ff',
              background: (!Object.keys(dirtyCells || {}).length || saving) ? '#e6f4ff' : '#1677ff',
              color: (!Object.keys(dirtyCells || {}).length || saving) ? '#1677ff' : '#fff',
              cursor: (!Object.keys(dirtyCells || {}).length || saving) ? 'not-allowed' : 'pointer',
            }}
          >
            {saving ? 'Saving...' : `Save changes (${Object.keys(dirtyCells || {}).length})`}
          </button>
        </div>
      </Space>
      <div style={{ fontSize: 12, color: '#888' }}>
        Table debug — rows: {rowData?.length || 0}, cols: {columns?.length || 0}
      </div>
      <div
        style={{
          maxHeight: '65vh',
          overflow: 'auto',
          border: '1px solid #e5e5e5',
          borderRadius: 6,
          width: '100%',
          userSelect: isSelecting ? 'none' : 'auto',
        }}
      >
        <table style={{ minWidth: totalWidth }}>
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.dataIndex}
                  style={{
                    position: 'sticky',
                    top: 0,
                    background: '#fafafa',
                    textAlign: 'left',
                    padding: '8px 12px',
                    borderBottom: '1px solid #e5e5e5',
                    whiteSpace: 'nowrap',
                    width: c.width,
                    minWidth: c.width,
                  }}
                >
                  {c.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columnCount} style={{ padding: '16px 24px' }}>
                  <Skeleton active paragraph={{ rows: 3 }} />
                </td>
              </tr>
            ) : (
              rowData.map((r, rowIndex) => (
                <tr key={r.key}>
                  {columns.map((c) => {
                    const isEditable = editableFields.includes(c.dataIndex);
                    const val = c.render ? c.render(r[c.dataIndex], r) : r[c.dataIndex] ?? '';
                    if (!isEditable) {
                      return (
                        <td
                          key={c.dataIndex}
                          style={{ padding: '6px 12px', borderBottom: '1px solid #f0f0f0' }}
                          onMouseDown={(e) => startSelection(rowIndex, c.dataIndex, e)}
                          onMouseEnter={() => extendSelection(rowIndex, c.dataIndex)}
                        >
                          {val || '-'}
                        </td>
                      );
                    }
                    const inputVal =
                      r[c.dataIndex] === '-' || r[c.dataIndex] == null ? '' : String(r[c.dataIndex]);
                    return (
                      <td
                        key={c.dataIndex}
                        style={{
                          padding: 0,
                          borderBottom: '1px solid #f0f0f0',
                          background: selectedCells.has(`${rowIndex}:${c.dataIndex}`) ? '#e6f4ff' : undefined,
                        }}
                        onMouseDown={(e) => startSelection(rowIndex, c.dataIndex, e)}
                        onMouseEnter={() => extendSelection(rowIndex, c.dataIndex)}
                      >
                        <input
                          value={inputVal}
                          onChange={(e) => updateCell(rowIndex, c.dataIndex, e.target.value)}
                          onPaste={(e) => handlePaste(e, rowIndex, c.dataIndex)}
                          onMouseDown={(e) => startSelection(rowIndex, c.dataIndex, e)}
                          onMouseEnter={() => extendSelection(rowIndex, c.dataIndex)}
                          onKeyDown={(e) => handleKeyDown(e, rowIndex, c.dataIndex)}
                          onFocus={() => setSelectedCells(new Set([`${rowIndex}:${c.dataIndex}`]))}
                          style={{
                            width: '100%',
                            boxSizing: 'border-box',
                            border: 'none',
                            outline: 'none',
                            padding: '6px 12px',
                            background: 'transparent',
                          }}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Pagination
          size="small"
          current={pagination.current}
          pageSize={pagination.pageSize}
          total={pagination.total}
          showSizeChanger
          pageSizeOptions={[5, 10, 20, 50, 100]}
          onChange={(page, pageSize) => fetchData(page, pageSize)}
        />
      </div>
    </div>
  );
};

export default ReportsByLocation;
